import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import {
  LayoutDashboard, FileText, Wand2, LayoutTemplate, History,
  Star, Settings, Loader2, Copy, Check, Download, Printer,
  ChevronRight, ChevronLeft, X, Sparkles, RefreshCw,
  Maximize2, Minimize2, Trash2, Heart, PenLine, Building2,
  Briefcase, User, Sliders, FileEdit, RotateCcw, Plus,
} from "lucide-react";
import { toast } from "sonner";

// ─── Config ──────────────────────────────────────────────────────────────────
const NAV = [
  { id: "dashboard", label: "Dashboard",         icon: LayoutDashboard },
  { id: "generate",  label: "Generate",           icon: Wand2 },
  { id: "letters",   label: "My Cover Letters",   icon: FileText },
  { id: "templates", label: "Templates",          icon: LayoutTemplate },
  { id: "settings",  label: "Settings",           icon: Settings },
];

const TONES = [
  "Professional", "Executive", "Technical", "Startup", "Creative",
  "Consulting", "Friendly", "Confident", "Corporate", "Minimal",
  "Recruiter Friendly", "Persuasive", "Formal",
];

const STYLES = [
  "Achievement Focused", "Leadership Focused", "Business Value Focused",
  "Technical", "Storytelling", "Concise", "Detailed", "Modern", "Consulting Style",
];

const LENGTHS = [
  { id: "Very Short", words: "~200 words", desc: "Quick and punchy" },
  { id: "Standard",   words: "~350 words", desc: "Ideal for most roles" },
  { id: "Detailed",   words: "~500 words", desc: "Senior positions" },
  { id: "Executive",  words: "~700 words", desc: "C-suite / Board level" },
];

const TEMPLATES = [
  { id: "Modern",     color: "#0055FF", light: false, desc: "Clean, contemporary" },
  { id: "Corporate",  color: "#2C2C2C", light: false, desc: "Traditional formal" },
  { id: "Executive",  color: "#1a1a3e", light: false, desc: "C-suite gravitas" },
  { id: "Minimal",    color: "#e5e5e5", light: true,  desc: "Pure whitespace" },
  { id: "Technical",  color: "#003366", light: false, desc: "Engineering roles" },
  { id: "Startup",    color: "#D3FF24", light: true,  desc: "Bold, energetic" },
  { id: "Consulting", color: "#1B4F72", light: false, desc: "McKinsey / Big 4" },
  { id: "Creative",   color: "#6B2FDB", light: false, desc: "Design, marketing" },
  { id: "Finance",    color: "#0A3D2B", light: false, desc: "Banking, fintech" },
  { id: "Healthcare", color: "#006B6B", light: false, desc: "Medical, pharma" },
  { id: "Government", color: "#1C3A5E", light: false, desc: "Public sector" },
  { id: "Academic",   color: "#4A2C8F", light: false, desc: "Research, academia" },
];

// ─── Helpers ─────────────────────────────────────────────────────────────────
const tkn = () => localStorage.getItem("token");

async function streamFetch(url, body, onDelta, onDone, onError) {
  try {
    const res = await fetch(url, {
      method: "POST",
      headers: { "Content-Type": "application/json", Authorization: `Bearer ${tkn()}` },
      body: JSON.stringify(body),
    });
    if (!res.ok) {
      const e = await res.json().catch(() => ({}));
      throw new Error(e.detail || "Request failed");
    }
    const reader = res.body.getReader();
    const dec = new TextDecoder();
    let buf = "";
    while (true) {
      const { done, value } = await reader.read();
      if (done) break;
      buf += dec.decode(value, { stream: true });
      const lines = buf.split("\n");
      buf = lines.pop();
      for (const line of lines) {
        if (!line.startsWith("data: ")) continue;
        const data = line.slice(6);
        if (data === "[DONE]") { onDone?.(); return; }
        try { onDelta?.(JSON.parse(data).delta || ""); } catch {}
      }
    }
    onDone?.();
  } catch (e) {
    onError?.(e);
  }
}

function wc(text) {
  return text.trim() ? text.trim().split(/\s+/).length : 0;
}

function downloadTxt(content, name) {
  const blob = new Blob([content], { type: "text/plain" });
  const url = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url;
  a.download = (name || "cover-letter") + ".txt";
  a.click();
  URL.revokeObjectURL(url);
}

function CopyBtn({ text, className = "" }) {
  const [copied, setCopied] = useState(false);
  const copy = () => { navigator.clipboard.writeText(text); setCopied(true); setTimeout(() => setCopied(false), 2000); };
  return (
    <button onClick={copy} className={`flex items-center gap-1.5 transition ${className}`}>
      {copied ? <Check className="w-3.5 h-3.5 text-[#D3FF24]" /> : <Copy className="w-3.5 h-3.5" />}
      {copied ? "Copied!" : "Copy"}
    </button>
  );
}

function TagInput({ tags, onChange, placeholder }) {
  const [input, setInput] = useState("");
  const add = () => {
    const ns = input.split(",").map(s => s.trim()).filter(Boolean);
    onChange([...new Set([...tags, ...ns])]);
    setInput("");
  };
  return (
    <div>
      <div className="flex flex-wrap gap-1.5 min-h-[36px] p-2 border border-white/10 mb-2">
        {tags.map(t => (
          <span key={t} className="flex items-center gap-1 border border-white/15 px-2 py-0.5 text-xs">
            {t}
            <button onClick={() => onChange(tags.filter(x => x !== t))} className="hover:text-red-400"><X className="w-2.5 h-2.5" /></button>
          </span>
        ))}
        {!tags.length && <span className="text-xs text-neutral-700">None added</span>}
      </div>
      <div className="flex gap-2">
        <input value={input} onChange={e => setInput(e.target.value)} onKeyDown={e => e.key === "Enter" && add()}
          className="flex-1 bg-black border border-white/10 px-3 py-1.5 text-xs focus:border-[#0055FF] outline-none"
          placeholder={placeholder} />
        <button onClick={add} className="border border-white/15 px-3 text-xs hover:border-[#0055FF] transition">Add</button>
      </div>
    </div>
  );
}

function StepIndicator({ step }) {
  const steps = [
    { n: 1, label: "Job Details",   icon: Building2 },
    { n: 2, label: "Your Details",  icon: User },
    { n: 3, label: "Customize",     icon: Sliders },
    { n: 4, label: "Generate",      icon: Wand2 },
  ];
  return (
    <div className="flex items-center mb-8">
      {steps.map(({ n, label, icon: Icon }, i) => {
        const done = step > n;
        const active = step === n;
        return (
          <React.Fragment key={n}>
            <div className={`flex items-center gap-2 text-xs transition ${active ? "text-white" : done ? "text-[#D3FF24]" : "text-neutral-600"}`}>
              <div className={`w-6 h-6 flex items-center justify-center text-[10px] font-black border shrink-0 ${active ? "bg-white text-black border-white" : done ? "bg-[#D3FF24] text-black border-[#D3FF24]" : "border-neutral-700"}`}>
                {done ? <Check className="w-3 h-3" /> : n}
              </div>
              <span className="hidden md:inline font-medium">{label}</span>
            </div>
            {i < 3 && <div className={`flex-1 mx-3 h-px ${step > n ? "bg-[#D3FF24]/30" : "bg-white/8"}`} />}
          </React.Fragment>
        );
      })}
    </div>
  );
}

function Field({ label, required, hint, children }) {
  return (
    <div>
      <label className="text-[10px] uppercase tracking-widest text-neutral-500 font-semibold">
        {label} {required && <span className="text-red-400">*</span>}
        {hint && <span className="normal-case text-neutral-700 tracking-normal ml-1">{hint}</span>}
      </label>
      <div className="mt-1">{children}</div>
    </div>
  );
}

const inputCls = "w-full bg-black border border-white/10 px-3 py-2.5 text-sm focus:border-[#0055FF] outline-none";
const textareaCls = "w-full bg-black border border-white/10 px-4 py-3 text-sm focus:border-[#0055FF] outline-none resize-none";

// ─── Document Preview ─────────────────────────────────────────────────────────
function DocumentPreview({ content }) {
  if (!content) return (
    <div className="bg-white text-gray-300 flex items-center justify-center" style={{ minHeight: 600 }}>
      <p className="text-gray-400 text-sm">Generate a cover letter to preview it here</p>
    </div>
  );
  return (
    <div className="bg-white text-gray-800 px-12 py-10 font-serif text-[13.5px] leading-relaxed shadow-xl" style={{ minHeight: 600, fontFamily: "'Georgia', serif" }}>
      {content.split("\n").map((line, i) => (
        line.trim() === ""
          ? <div key={i} className="h-3" />
          : <p key={i}>{line}</p>
      ))}
    </div>
  );
}

// ─── Dashboard Section ────────────────────────────────────────────────────────
function DashboardSection({ letters, loading, setSection }) {
  const favorites = letters.filter(l => l.is_favorite).length;
  const thisWeek = letters.filter(l => {
    const d = new Date(l.created_at);
    return (Date.now() - d) < 7 * 24 * 60 * 60 * 1000;
  }).length;

  return (
    <div>
      <div className="mb-8">
        <h2 className="font-display text-3xl font-black tracking-tighter mb-1">Dashboard</h2>
        <p className="text-neutral-500 text-sm">Your cover letter command center</p>
      </div>

      <div className="grid grid-cols-4 gap-4 mb-8">
        {[
          { label: "Total Letters",   value: letters.length,  color: "text-white" },
          { label: "Favorites",       value: favorites,       color: "text-[#D3FF24]" },
          { label: "This Week",       value: thisWeek,        color: "text-[#0055FF]" },
          { label: "Templates Used",  value: new Set(letters.map(l => l.template).filter(Boolean)).size, color: "text-purple-400" },
        ].map(s => (
          <div key={s.label} className="border border-white/10 p-5">
            <div className="text-[10px] uppercase tracking-widest text-neutral-500 font-semibold mb-2">{s.label}</div>
            <div className={`text-3xl font-black ${s.color}`}>{loading ? "—" : s.value}</div>
          </div>
        ))}
      </div>

      <div className="grid grid-cols-2 gap-4 mb-8">
        <button onClick={() => setSection("generate")}
          className="border border-[#0055FF] bg-[#0055FF]/5 p-6 text-left hover:bg-[#0055FF]/10 transition group">
          <Wand2 className="w-8 h-8 text-[#0055FF] mb-3" strokeWidth={1.5} />
          <div className="font-bold text-white text-lg">Generate New Cover Letter</div>
          <div className="text-sm text-neutral-500 mt-1">AI-powered, personalized in minutes</div>
          <div className="flex items-center gap-1 text-xs text-[#0055FF] mt-3">Start now <ChevronRight className="w-3.5 h-3.5" /></div>
        </button>
        <button onClick={() => setSection("templates")}
          className="border border-white/10 p-6 text-left hover:border-white/25 transition group">
          <LayoutTemplate className="w-8 h-8 text-neutral-500 mb-3 group-hover:text-white transition" strokeWidth={1.5} />
          <div className="font-bold text-white text-lg">Browse Templates</div>
          <div className="text-sm text-neutral-500 mt-1">12 professional styles to choose from</div>
          <div className="flex items-center gap-1 text-xs text-neutral-500 mt-3 group-hover:text-white transition">View all <ChevronRight className="w-3.5 h-3.5" /></div>
        </button>
      </div>

      {letters.length > 0 && (
        <div className="border border-white/10">
          <div className="px-5 py-3 border-b border-white/10 flex items-center justify-between">
            <div className="text-[10px] uppercase tracking-widest font-semibold text-neutral-400">Recent Cover Letters</div>
            <button onClick={() => setSection("letters")} className="text-xs text-[#D3FF24] hover:underline">View all →</button>
          </div>
          <div className="divide-y divide-white/5">
            {letters.slice(0, 5).map(l => (
              <div key={l.id} className="flex items-center gap-4 px-5 py-3.5">
                <FileText className="w-4 h-4 text-neutral-600 shrink-0" strokeWidth={1.75} />
                <div className="flex-1 min-w-0">
                  <div className="text-sm font-medium text-white truncate">{l.title}</div>
                  <div className="text-xs text-neutral-600 mt-0.5">{l.word_count || 0} words · {l.tone || "Professional"}</div>
                </div>
                <div className="text-xs text-neutral-700">{new Date(l.updated_at).toLocaleDateString()}</div>
                {l.is_favorite && <Star className="w-3.5 h-3.5 text-[#D3FF24] fill-[#D3FF24] shrink-0" />}
              </div>
            ))}
          </div>
        </div>
      )}

      {!loading && !letters.length && (
        <div className="border border-white/10 p-16 text-center">
          <FileText className="w-10 h-10 text-neutral-800 mx-auto mb-4" strokeWidth={1.25} />
          <p className="text-neutral-500 text-sm font-semibold">No cover letters yet</p>
          <p className="text-neutral-700 text-xs mt-1">Generate your first one — takes under 2 minutes</p>
          <button onClick={() => setSection("generate")} className="mt-4 bg-[#0055FF] text-white px-5 py-2 text-xs font-semibold uppercase tracking-wider hover:bg-[#0044CC] transition">
            Get Started
          </button>
        </div>
      )}
    </div>
  );
}

// ─── Generate Section ─────────────────────────────────────────────────────────
function GenerateSection({ linkedInProfile, onSaved }) {
  const [step, setStep] = useState(1);

  const [job, setJob] = useState({
    company: "", job_title: "", hiring_manager: "", industry: "",
    employment_type: "Full-time", work_mode: "Onsite", job_description: "",
  });

  const [profile, setProfile] = useState({
    full_name: "", current_role: "", years_experience: 0,
    education: "", skills: [], achievements: [],
  });

  const [customize, setCustomize] = useState({
    tone: "Professional", style: "Achievement Focused",
    length: "Standard", template: "Modern",
  });

  const [variations, setVariations] = useState({ A: "", B: "", C: "" });
  const [activeVar, setActiveVar] = useState("A");
  const [loading, setLoading] = useState(false);
  const [improving, setImproving] = useState("");
  const [prevContent, setPrevContent] = useState("");
  const [previewMode, setPreviewMode] = useState(false);
  const [clId, setClId] = useState(null);
  const [saving, setSaving] = useState(false);
  const [achInput, setAchInput] = useState("");

  const content = variations[activeVar];
  const words = wc(content);
  const readingTime = Math.ceil(words / 200) || 1;

  // Pre-fill from LinkedIn profile
  useEffect(() => {
    if (linkedInProfile && Object.keys(linkedInProfile).length) {
      setProfile(p => ({
        ...p,
        full_name: linkedInProfile.name || p.full_name,
        current_role: linkedInProfile.headline || p.current_role,
        skills: linkedInProfile.skills?.length ? linkedInProfile.skills : p.skills,
      }));
    }
  }, [linkedInProfile]);

  const setContent = (text) => setVariations(v => ({ ...v, [activeVar]: text }));

  const generate = () => {
    const target = activeVar;
    setLoading(true);
    setPrevContent(variations[target]);
    setVariations(v => ({ ...v, [target]: "" }));
    streamFetch("/api/coverletter/generate",
      { ...job, ...profile, tone: customize.tone, writing_style: customize.style, length: customize.length },
      (d) => setVariations(v => ({ ...v, [target]: (v[target] || "") + d })),
      () => setLoading(false),
      (e) => { toast.error(e.message); setLoading(false); }
    );
  };

  const improve = (action) => {
    if (!content) { toast.error("Generate a cover letter first"); return; }
    const target = (action === "variation_b") ? "B" : (action === "variation_c") ? "C" : activeVar;
    setImproving(action);
    setPrevContent(variations[target]);
    if (action === "variation_b") setActiveVar("B");
    if (action === "variation_c") setActiveVar("C");
    setVariations(v => ({ ...v, [target]: "" }));
    streamFetch("/api/coverletter/improve",
      { content: variations[activeVar], action, job_description: job.job_description },
      (d) => setVariations(v => ({ ...v, [target]: (v[target] || "") + d })),
      () => setImproving(""),
      (e) => { toast.error(e.message); setImproving(""); }
    );
  };

  const undo = () => { if (prevContent) { setContent(prevContent); setPrevContent(""); } };

  const save = async () => {
    if (!content) { toast.error("Nothing to save"); return; }
    setSaving(true);
    try {
      const res = await fetch("/api/coverletter/save", {
        method: "POST",
        headers: { "Content-Type": "application/json", Authorization: `Bearer ${tkn()}` },
        body: JSON.stringify({
          cl_id: clId, content,
          company: job.company, job_title: job.job_title,
          tone: customize.tone, template: customize.template,
          word_count: words,
        }),
      });
      if (!res.ok) throw new Error("Save failed");
      const data = await res.json();
      setClId(data.cl_id);
      toast.success("Cover letter saved");
      onSaved?.();
    } catch (e) {
      toast.error(e.message);
    } finally {
      setSaving(false);
    }
  };

  const canNext1 = job.company.trim() && job.job_title.trim() && job.job_description.trim();
  const canNext2 = profile.full_name.trim();

  // ── Step 1: Job Details ──
  const renderStep1 = () => (
    <div className="space-y-5 max-w-2xl">
      <div className="grid grid-cols-2 gap-4">
        <Field label="Company Name" required>
          <input value={job.company} onChange={e => setJob(j => ({ ...j, company: e.target.value }))}
            className={inputCls} placeholder="Google" />
        </Field>
        <Field label="Job Title" required>
          <input value={job.job_title} onChange={e => setJob(j => ({ ...j, job_title: e.target.value }))}
            className={inputCls} placeholder="Senior Software Engineer" />
        </Field>
      </div>
      <div className="grid grid-cols-2 gap-4">
        <Field label="Hiring Manager" hint="(optional)">
          <input value={job.hiring_manager} onChange={e => setJob(j => ({ ...j, hiring_manager: e.target.value }))}
            className={inputCls} placeholder="Sarah Chen" />
        </Field>
        <Field label="Industry">
          <input value={job.industry} onChange={e => setJob(j => ({ ...j, industry: e.target.value }))}
            className={inputCls} placeholder="Technology / SaaS" />
        </Field>
      </div>
      <div className="grid grid-cols-2 gap-4">
        <Field label="Employment Type">
          <select value={job.employment_type} onChange={e => setJob(j => ({ ...j, employment_type: e.target.value }))}
            className={inputCls + " bg-black"}>
            {["Full-time", "Part-time", "Contract", "Freelance", "Internship"].map(t => <option key={t}>{t}</option>)}
          </select>
        </Field>
        <Field label="Work Mode">
          <select value={job.work_mode} onChange={e => setJob(j => ({ ...j, work_mode: e.target.value }))}
            className={inputCls + " bg-black"}>
            {["Onsite", "Remote", "Hybrid"].map(t => <option key={t}>{t}</option>)}
          </select>
        </Field>
      </div>
      <Field label="Job Description" required hint="— paste the full JD for best results">
        <textarea rows={10} value={job.job_description} onChange={e => setJob(j => ({ ...j, job_description: e.target.value }))}
          className={textareaCls} placeholder="Paste the full job description here…" />
      </Field>
      <button onClick={() => setStep(2)} disabled={!canNext1}
        className="flex items-center gap-2 bg-[#0055FF] text-white px-6 py-3 text-xs font-semibold uppercase tracking-wider hover:bg-[#0044CC] transition disabled:opacity-40">
        Continue <ChevronRight className="w-4 h-4" />
      </button>
    </div>
  );

  // ── Step 2: Your Details ──
  const renderStep2 = () => (
    <div className="space-y-5 max-w-2xl">
      {linkedInProfile?.name && (
        <div className="border border-[#D3FF24]/20 bg-[#D3FF24]/5 px-4 py-3 text-xs text-[#D3FF24]">
          Pre-filled from your LinkedIn profile — edit as needed
        </div>
      )}
      <div className="grid grid-cols-2 gap-4">
        <Field label="Full Name" required>
          <input value={profile.full_name} onChange={e => setProfile(p => ({ ...p, full_name: e.target.value }))}
            className={inputCls} placeholder="Alex Johnson" />
        </Field>
        <Field label="Current / Recent Role">
          <input value={profile.current_role} onChange={e => setProfile(p => ({ ...p, current_role: e.target.value }))}
            className={inputCls} placeholder="Software Engineer at Stripe" />
        </Field>
      </div>
      <div className="grid grid-cols-2 gap-4">
        <Field label="Years of Experience">
          <input type="number" min={0} max={50} value={profile.years_experience}
            onChange={e => setProfile(p => ({ ...p, years_experience: parseInt(e.target.value) || 0 }))}
            className={inputCls} />
        </Field>
        <Field label="Education" hint="(optional)">
          <input value={profile.education} onChange={e => setProfile(p => ({ ...p, education: e.target.value }))}
            className={inputCls} placeholder="B.S. Computer Science, MIT" />
        </Field>
      </div>
      <Field label="Key Skills" hint="— the more specific, the better">
        <TagInput tags={profile.skills} onChange={skills => setProfile(p => ({ ...p, skills }))}
          placeholder="React, Node.js, Python, AWS — comma separated, then Enter" />
      </Field>
      <Field label="Top Achievements" hint="— one per line, AI will quantify and polish these">
        <div className="flex gap-2 mb-2">
          <input value={achInput} onChange={e => setAchInput(e.target.value)}
            onKeyDown={e => { if (e.key === "Enter" && achInput.trim()) { setProfile(p => ({ ...p, achievements: [...p.achievements, achInput.trim()] })); setAchInput(""); } }}
            className={inputCls} placeholder="Led migration that cut API latency by 60% — then press Enter" />
          <button onClick={() => { if (achInput.trim()) { setProfile(p => ({ ...p, achievements: [...p.achievements, achInput.trim()] })); setAchInput(""); } }}
            className="border border-white/15 px-3 text-sm hover:border-[#0055FF] transition shrink-0"><Plus className="w-4 h-4" /></button>
        </div>
        <div className="space-y-1.5">
          {profile.achievements.map((a, i) => (
            <div key={i} className="flex items-start gap-2 text-xs text-neutral-400 border border-white/8 px-3 py-2">
              <span className="text-neutral-600 shrink-0 mt-0.5">•</span>
              <span className="flex-1">{a}</span>
              <button onClick={() => setProfile(p => ({ ...p, achievements: p.achievements.filter((_, j) => j !== i) }))}
                className="text-neutral-600 hover:text-red-400 shrink-0"><X className="w-3 h-3" /></button>
            </div>
          ))}
        </div>
      </Field>
      <div className="flex gap-3">
        <button onClick={() => setStep(1)} className="flex items-center gap-2 border border-white/15 text-neutral-400 px-5 py-2.5 text-xs font-semibold uppercase tracking-wider hover:border-white/30 hover:text-white transition">
          <ChevronLeft className="w-4 h-4" /> Back
        </button>
        <button onClick={() => setStep(3)} disabled={!canNext2}
          className="flex items-center gap-2 bg-[#0055FF] text-white px-6 py-2.5 text-xs font-semibold uppercase tracking-wider hover:bg-[#0044CC] transition disabled:opacity-40">
          Continue <ChevronRight className="w-4 h-4" />
        </button>
      </div>
    </div>
  );

  // ── Step 3: Customize ──
  const renderStep3 = () => (
    <div className="space-y-8 max-w-2xl">
      <div>
        <div className="text-[10px] uppercase tracking-widest text-neutral-500 font-semibold mb-3">Tone</div>
        <div className="flex flex-wrap gap-2">
          {TONES.map(t => (
            <button key={t} onClick={() => setCustomize(c => ({ ...c, tone: t }))}
              className={`px-3 py-1.5 text-xs border transition ${customize.tone === t ? "border-[#D3FF24] text-[#D3FF24] bg-[#D3FF24]/5" : "border-white/15 text-neutral-400 hover:border-white/30"}`}>
              {t}
            </button>
          ))}
        </div>
      </div>
      <div>
        <div className="text-[10px] uppercase tracking-widest text-neutral-500 font-semibold mb-3">Writing Style</div>
        <div className="flex flex-wrap gap-2">
          {STYLES.map(s => (
            <button key={s} onClick={() => setCustomize(c => ({ ...c, style: s }))}
              className={`px-3 py-1.5 text-xs border transition ${customize.style === s ? "border-[#0055FF] text-[#0055FF] bg-[#0055FF]/5" : "border-white/15 text-neutral-400 hover:border-white/30"}`}>
              {s}
            </button>
          ))}
        </div>
      </div>
      <div>
        <div className="text-[10px] uppercase tracking-widest text-neutral-500 font-semibold mb-3">Length</div>
        <div className="grid grid-cols-4 gap-3">
          {LENGTHS.map(l => (
            <button key={l.id} onClick={() => setCustomize(c => ({ ...c, length: l.id }))}
              className={`p-4 border text-left transition ${customize.length === l.id ? "border-[#D3FF24] bg-[#D3FF24]/5" : "border-white/10 hover:border-white/25"}`}>
              <div className={`text-sm font-bold ${customize.length === l.id ? "text-[#D3FF24]" : "text-white"}`}>{l.id}</div>
              <div className="text-[10px] text-neutral-400 mt-0.5">{l.words}</div>
              <div className="text-[10px] text-neutral-600 mt-0.5">{l.desc}</div>
            </button>
          ))}
        </div>
      </div>
      <div>
        <div className="text-[10px] uppercase tracking-widest text-neutral-500 font-semibold mb-3">Template Style</div>
        <div className="grid grid-cols-4 gap-3">
          {TEMPLATES.map(t => (
            <button key={t.id} onClick={() => setCustomize(c => ({ ...c, template: t.id }))}
              className={`p-3 border text-left transition ${customize.template === t.id ? "border-[#D3FF24]" : "border-white/10 hover:border-white/25"}`}>
              <div className="h-20 relative overflow-hidden mb-2 bg-[#0A0A0A]">
                <div className="h-2 w-full" style={{ background: t.color }} />
                <div className="p-2 space-y-1">
                  {[...Array(4)].map((_, i) => (
                    <div key={i} className="bg-white/10 h-1 rounded" style={{ width: i === 0 ? "60%" : i === 3 ? "40%" : "90%" }} />
                  ))}
                </div>
                {customize.template === t.id && (
                  <div className="absolute top-1.5 right-1.5 w-4 h-4 bg-[#D3FF24] flex items-center justify-center">
                    <Check className="w-2.5 h-2.5 text-black" />
                  </div>
                )}
              </div>
              <div className="text-xs font-semibold text-white">{t.id}</div>
              <div className="text-[10px] text-neutral-500 mt-0.5">{t.desc}</div>
            </button>
          ))}
        </div>
      </div>
      <div className="flex gap-3">
        <button onClick={() => setStep(2)} className="flex items-center gap-2 border border-white/15 text-neutral-400 px-5 py-2.5 text-xs font-semibold uppercase tracking-wider hover:border-white/30 hover:text-white transition">
          <ChevronLeft className="w-4 h-4" /> Back
        </button>
        <button onClick={() => setStep(4)}
          className="flex items-center gap-2 bg-[#0055FF] text-white px-6 py-2.5 text-xs font-semibold uppercase tracking-wider hover:bg-[#0044CC] transition">
          Continue <ChevronRight className="w-4 h-4" />
        </button>
      </div>
    </div>
  );

  // ── Step 4: Generate & Edit ──
  const renderStep4 = () => {
    const ActionBtn = ({ label, action, icon: Icon, variant = "default" }) => {
      const isRunning = improving === action;
      const variants = {
        default: "border-white/10 text-neutral-400 hover:border-white/25 hover:text-white",
        yellow:  "border-[#D3FF24]/20 text-[#D3FF24] hover:bg-[#D3FF24]/5",
        blue:    "border-[#0055FF]/30 text-[#0055FF] hover:bg-[#0055FF]/5",
      };
      return (
        <button onClick={() => improve(action)} disabled={loading || !!improving}
          className={`flex items-center gap-1.5 border px-3 py-1.5 text-xs transition ${variants[variant]} disabled:opacity-40`}>
          {isRunning ? <Loader2 className="w-3 h-3 animate-spin" /> : Icon && <Icon className="w-3 h-3" />}
          {label}
        </button>
      );
    };

    return (
      <div>
        {/* Top controls */}
        <div className="flex items-center gap-3 mb-4 flex-wrap">
          <button onClick={generate} disabled={loading || !!improving}
            className="flex items-center gap-2 bg-[#0055FF] text-white px-5 py-2 text-xs font-semibold uppercase tracking-wider hover:bg-[#0044CC] transition disabled:opacity-50">
            {loading ? <><Loader2 className="w-3.5 h-3.5 animate-spin" /> Generating…</> : <><Sparkles className="w-3.5 h-3.5" /> {content ? "Regenerate" : "Generate Cover Letter"}</>}
          </button>
          {prevContent && (
            <button onClick={undo} className="flex items-center gap-1.5 border border-white/15 text-neutral-400 px-3 py-2 text-xs hover:text-white hover:border-white/30 transition">
              <RotateCcw className="w-3 h-3" /> Undo
            </button>
          )}
          <button onClick={() => setStep(3)} className="flex items-center gap-1.5 border border-white/15 text-neutral-400 px-3 py-2 text-xs hover:text-white transition">
            <ChevronLeft className="w-3 h-3" /> Back
          </button>
          <button onClick={() => setPreviewMode(m => !m)}
            className={`flex items-center gap-1.5 border px-3 py-2 text-xs transition ml-auto ${previewMode ? "border-white/30 text-white" : "border-white/10 text-neutral-500 hover:text-white"}`}>
            {previewMode ? <><Minimize2 className="w-3 h-3" /> Edit</> : <><Maximize2 className="w-3 h-3" /> Preview</>}
          </button>
        </div>

        {/* Improve toolbar */}
        {content && (
          <div className="border border-white/8 p-3 mb-4 space-y-2">
            <div className="text-[9px] uppercase tracking-widest text-neutral-700 font-semibold mb-2">Transform</div>
            <div className="flex flex-wrap gap-2">
              <ActionBtn label="Rewrite"   action="rewrite"   icon={RefreshCw} />
              <ActionBtn label="Improve"   action="improve"   icon={PenLine} />
              <ActionBtn label="Expand"    action="expand"    icon={Plus} />
              <ActionBtn label="Shorten"   action="shorten"   icon={Minimize2} />
              <ActionBtn label="ATS Optimize" action="ats"   icon={Sparkles} variant="yellow" />
              <ActionBtn label="Humanize"  action="humanize"  icon={User} variant="yellow" />
              <ActionBtn label="More Confident" action="confident" icon={Briefcase} variant="yellow" />
              <ActionBtn label="Fix Grammar" action="grammar" icon={Check} />
              <ActionBtn label="Add Metrics" action="metrics" icon={FileEdit} variant="blue" />
            </div>
            <div className="flex flex-wrap gap-2 pt-1">
              <div className="text-[9px] uppercase tracking-widest text-neutral-700 font-semibold mr-1 self-center">Variations</div>
              {["A", "B", "C"].map(v => (
                <button key={v} onClick={() => { if (v !== "A" && !variations[v]) improve(`variation_${v.toLowerCase()}`); else setActiveVar(v); }}
                  disabled={loading || !!improving}
                  className={`w-8 h-7 text-xs font-black border transition ${activeVar === v ? "border-[#D3FF24] text-[#D3FF24] bg-[#D3FF24]/5" : "border-white/15 text-neutral-500 hover:border-white/30"} ${variations[v] ? "" : "opacity-50"}`}>
                  {v}
                </button>
              ))}
              {activeVar !== "A" && variations.A && (
                <button onClick={() => setActiveVar("A")} className="text-xs text-neutral-600 hover:text-white transition">← back to A</button>
              )}
            </div>
          </div>
        )}

        {/* Editor / Preview */}
        {previewMode ? (
          <DocumentPreview content={content} />
        ) : (
          <div className="relative">
            <textarea
              value={content}
              onChange={e => setContent(e.target.value)}
              className="w-full bg-[#050505] border border-white/10 px-6 py-5 text-sm text-neutral-200 focus:border-white/20 outline-none resize-none leading-relaxed font-mono"
              style={{ minHeight: 480 }}
              placeholder="Your cover letter will appear here. Click 'Generate Cover Letter' to start, or type directly…"
            />
            {(loading || improving) && !content && (
              <div className="absolute inset-0 flex items-center justify-center pointer-events-none">
                <div className="text-center">
                  <Loader2 className="w-8 h-8 animate-spin mx-auto mb-2 text-[#D3FF24]" />
                  <p className="text-neutral-500 text-xs">AI is writing your cover letter…</p>
                </div>
              </div>
            )}
          </div>
        )}

        {/* Stats + Export */}
        {content && (
          <div className="flex items-center gap-4 mt-3 border-t border-white/8 pt-3">
            <span className="text-xs text-neutral-600">{words} words</span>
            <span className="text-neutral-800">·</span>
            <span className="text-xs text-neutral-600">{readingTime} min read</span>
            <span className="text-neutral-800">·</span>
            <span className={`text-xs ${words > 600 ? "text-amber-400" : "text-[#D3FF24]"}`}>{LENGTHS.find(l => l.id === customize.length)?.words || ""} target</span>
            <div className="ml-auto flex items-center gap-2">
              <CopyBtn text={content} className="border border-white/10 px-3 py-1.5 text-xs text-neutral-400 hover:text-white hover:border-white/25" />
              <button onClick={() => downloadTxt(content, `cover-letter-${job.company}`)}
                className="flex items-center gap-1.5 border border-white/10 px-3 py-1.5 text-xs text-neutral-400 hover:text-white hover:border-white/25 transition">
                <Download className="w-3.5 h-3.5" /> .txt
              </button>
              <button onClick={() => window.print()}
                className="flex items-center gap-1.5 border border-white/10 px-3 py-1.5 text-xs text-neutral-400 hover:text-white hover:border-white/25 transition">
                <Printer className="w-3.5 h-3.5" /> Print
              </button>
              <button onClick={save} disabled={saving}
                className="flex items-center gap-1.5 bg-[#D3FF24] text-black px-4 py-1.5 text-xs font-bold uppercase tracking-wider hover:brightness-110 transition disabled:opacity-60">
                {saving ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Check className="w-3.5 h-3.5" />}
                {clId ? "Update" : "Save"}
              </button>
            </div>
          </div>
        )}
      </div>
    );
  };

  return (
    <div>
      <div className="mb-6">
        <h2 className="font-display text-3xl font-black tracking-tighter mb-1">Generate Cover Letter</h2>
        <p className="text-neutral-500 text-sm">AI-powered, personalized, ATS-optimized — ready in under 2 minutes</p>
      </div>
      <StepIndicator step={step} />
      {step === 1 && renderStep1()}
      {step === 2 && renderStep2()}
      {step === 3 && renderStep3()}
      {step === 4 && renderStep4()}
    </div>
  );
}

// ─── My Cover Letters ─────────────────────────────────────────────────────────
function LettersSection({ letters, loading, onDelete, onFavorite, setSection }) {
  const [search, setSearch] = useState("");

  const filtered = letters.filter(l =>
    !search || l.title?.toLowerCase().includes(search.toLowerCase()) ||
    l.company?.toLowerCase().includes(search.toLowerCase()) ||
    l.job_title?.toLowerCase().includes(search.toLowerCase())
  );

  const handleDelete = async (id) => {
    if (!window.confirm("Delete this cover letter?")) return;
    try {
      const res = await fetch(`/api/coverletter/${id}`, {
        method: "DELETE",
        headers: { Authorization: `Bearer ${tkn()}` },
      });
      if (!res.ok) throw new Error("Delete failed");
      toast.success("Deleted");
      onDelete(id);
    } catch (e) {
      toast.error(e.message);
    }
  };

  const handleFavorite = async (id) => {
    try {
      const res = await fetch(`/api/coverletter/${id}/favorite`, {
        method: "PATCH",
        headers: { Authorization: `Bearer ${tkn()}` },
      });
      if (!res.ok) throw new Error("Failed");
      const data = await res.json();
      onFavorite(id, data.is_favorite);
    } catch (e) {
      toast.error(e.message);
    }
  };

  return (
    <div>
      <div className="flex items-center justify-between mb-6">
        <div>
          <h2 className="font-display text-3xl font-black tracking-tighter mb-1">My Cover Letters</h2>
          <p className="text-neutral-500 text-sm">{letters.length} saved</p>
        </div>
        <button onClick={() => setSection("generate")}
          className="flex items-center gap-2 bg-[#0055FF] text-white px-4 py-2 text-xs font-semibold uppercase tracking-wider hover:bg-[#0044CC] transition">
          <Plus className="w-3.5 h-3.5" /> New
        </button>
      </div>

      <input value={search} onChange={e => setSearch(e.target.value)}
        className="w-full bg-black border border-white/10 px-4 py-2.5 text-sm focus:border-[#0055FF] outline-none mb-5"
        placeholder="Search by company, role, or title…" />

      {loading && (
        <div className="flex items-center justify-center py-16">
          <Loader2 className="w-6 h-6 animate-spin text-neutral-600" />
        </div>
      )}

      {!loading && !filtered.length && (
        <div className="border border-white/10 p-16 text-center">
          <FileText className="w-10 h-10 text-neutral-800 mx-auto mb-3" strokeWidth={1.25} />
          <p className="text-neutral-500 text-sm">{search ? "No results found" : "No cover letters yet"}</p>
          {!search && <button onClick={() => setSection("generate")} className="mt-4 text-xs text-[#D3FF24] hover:underline">Generate your first one →</button>}
        </div>
      )}

      <div className="space-y-2">
        {filtered.map(l => (
          <div key={l.id} className="border border-white/10 hover:border-white/20 transition">
            <div className="flex items-center gap-4 px-5 py-4">
              <FileText className="w-4 h-4 text-neutral-600 shrink-0" strokeWidth={1.75} />
              <div className="flex-1 min-w-0">
                <div className="text-sm font-semibold text-white truncate">{l.title}</div>
                <div className="flex items-center gap-3 mt-0.5">
                  {l.tone && <span className="text-[10px] border border-white/10 px-1.5 py-0.5 text-neutral-500">{l.tone}</span>}
                  {l.template && <span className="text-[10px] text-neutral-600">{l.template}</span>}
                  <span className="text-[10px] text-neutral-700">{l.word_count || 0} words</span>
                  <span className="text-[10px] text-neutral-700">{new Date(l.updated_at).toLocaleDateString()}</span>
                </div>
              </div>
              <div className="flex items-center gap-1">
                <button onClick={() => handleFavorite(l.id)}
                  className={`p-2 transition ${l.is_favorite ? "text-[#D3FF24]" : "text-neutral-600 hover:text-[#D3FF24]"}`}>
                  <Star className={`w-3.5 h-3.5 ${l.is_favorite ? "fill-[#D3FF24]" : ""}`} />
                </button>
                <button onClick={() => {
                  navigator.clipboard.writeText(l.content);
                  toast.success("Copied to clipboard");
                }} className="p-2 text-neutral-600 hover:text-white transition">
                  <Copy className="w-3.5 h-3.5" />
                </button>
                <button onClick={() => downloadTxt(l.content, l.title)}
                  className="p-2 text-neutral-600 hover:text-white transition">
                  <Download className="w-3.5 h-3.5" />
                </button>
                <button onClick={() => handleDelete(l.id)}
                  className="p-2 text-neutral-600 hover:text-red-400 transition">
                  <Trash2 className="w-3.5 h-3.5" />
                </button>
              </div>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}

// ─── Templates Section ────────────────────────────────────────────────────────
function TemplatesSection({ onSelectTemplate, setSection }) {
  return (
    <div>
      <div className="mb-6">
        <h2 className="font-display text-3xl font-black tracking-tighter mb-1">Templates</h2>
        <p className="text-neutral-500 text-sm">12 professional styles — each optimized for specific industries and seniority levels</p>
      </div>
      <div className="grid grid-cols-3 gap-4">
        {TEMPLATES.map(t => (
          <button key={t.id} onClick={() => { onSelectTemplate(t.id); setSection("generate"); }}
            className="border border-white/10 p-4 text-left hover:border-white/30 transition group">
            <div className="h-32 bg-[#0A0A0A] relative overflow-hidden mb-3">
              <div className="h-2.5 w-full" style={{ background: t.color }} />
              <div className="p-4 space-y-2">
                <div className="bg-white/20 h-1.5 rounded w-2/3" />
                <div className="bg-white/10 h-1 rounded w-full" />
                <div className="bg-white/10 h-1 rounded w-full" />
                <div className="bg-white/10 h-1 rounded w-3/4" />
                <div className="mt-2 bg-white/5 h-1 rounded w-full" />
                <div className="bg-white/5 h-1 rounded w-full" />
                <div className="bg-white/5 h-1 rounded w-1/2" />
              </div>
            </div>
            <div className="font-bold text-white group-hover:text-[#D3FF24] transition text-sm">{t.id}</div>
            <div className="text-xs text-neutral-500 mt-0.5">{t.desc}</div>
            <div className="text-[10px] text-[#0055FF] mt-2 opacity-0 group-hover:opacity-100 transition">Use this template →</div>
          </button>
        ))}
      </div>
    </div>
  );
}

// ─── Settings Section ─────────────────────────────────────────────────────────
function SettingsSection({ defaults, setDefaults }) {
  return (
    <div>
      <div className="mb-6">
        <h2 className="font-display text-3xl font-black tracking-tighter mb-1">Settings</h2>
        <p className="text-neutral-500 text-sm">Default preferences for all new cover letters</p>
      </div>
      <div className="border border-white/10 p-6 max-w-lg space-y-5">
        <div>
          <label className="text-[10px] uppercase tracking-widest text-neutral-500 font-semibold">Default Tone</label>
          <select value={defaults.tone} onChange={e => setDefaults(d => ({ ...d, tone: e.target.value }))}
            className="mt-1 w-full bg-black border border-white/10 px-3 py-2.5 text-sm focus:border-[#0055FF] outline-none">
            {TONES.map(t => <option key={t}>{t}</option>)}
          </select>
        </div>
        <div>
          <label className="text-[10px] uppercase tracking-widest text-neutral-500 font-semibold">Default Writing Style</label>
          <select value={defaults.style} onChange={e => setDefaults(d => ({ ...d, style: e.target.value }))}
            className="mt-1 w-full bg-black border border-white/10 px-3 py-2.5 text-sm focus:border-[#0055FF] outline-none">
            {STYLES.map(s => <option key={s}>{s}</option>)}
          </select>
        </div>
        <div>
          <label className="text-[10px] uppercase tracking-widest text-neutral-500 font-semibold">Default Length</label>
          <select value={defaults.length} onChange={e => setDefaults(d => ({ ...d, length: e.target.value }))}
            className="mt-1 w-full bg-black border border-white/10 px-3 py-2.5 text-sm focus:border-[#0055FF] outline-none">
            {LENGTHS.map(l => <option key={l.id}>{l.id}</option>)}
          </select>
        </div>
        <div>
          <label className="text-[10px] uppercase tracking-widest text-neutral-500 font-semibold">Default Template</label>
          <select value={defaults.template} onChange={e => setDefaults(d => ({ ...d, template: e.target.value }))}
            className="mt-1 w-full bg-black border border-white/10 px-3 py-2.5 text-sm focus:border-[#0055FF] outline-none">
            {TEMPLATES.map(t => <option key={t.id}>{t.id}</option>)}
          </select>
        </div>
        <div className="text-xs text-neutral-600 pt-2 border-t border-white/8">
          These defaults pre-populate the Customize step when you generate a new cover letter.
        </div>
      </div>
    </div>
  );
}

// ─── Main ─────────────────────────────────────────────────────────────────────
export default function CoverLetterGenerator() {
  const [section, setSection] = useState("generate");
  const [linkedInProfile, setLinkedInProfile] = useState({});
  const [letters, setLetters] = useState([]);
  const [lettersLoading, setLettersLoading] = useState(true);
  const [defaults, setDefaults] = useState({
    tone: "Professional", style: "Achievement Focused",
    length: "Standard", template: "Modern",
  });

  useEffect(() => {
    // Load LinkedIn profile for pre-fill
    fetch("/api/linkedin/profile", { headers: { Authorization: `Bearer ${tkn()}` } })
      .then(r => r.json())
      .then(d => { if (d && Object.keys(d).length) setLinkedInProfile(d); })
      .catch(() => {});

    // Load saved cover letters
    fetch("/api/coverletter/list", { headers: { Authorization: `Bearer ${tkn()}` } })
      .then(r => r.json())
      .then(d => { setLetters(Array.isArray(d) ? d : []); setLettersLoading(false); })
      .catch(() => setLettersLoading(false));
  }, []);

  const refreshLetters = () => {
    fetch("/api/coverletter/list", { headers: { Authorization: `Bearer ${tkn()}` } })
      .then(r => r.json())
      .then(d => setLetters(Array.isArray(d) ? d : []))
      .catch(() => {});
  };

  return (
    <div className="flex min-h-screen">
      {/* Sub-nav */}
      <aside className="w-52 shrink-0 border-r border-white/10 py-5 sticky top-0 h-screen overflow-y-auto">
        <div className="px-4 mb-5 pb-4 border-b border-white/10">
          <div className="flex items-center gap-2">
            <div className="w-6 h-6 bg-[#0055FF] flex items-center justify-center shrink-0">
              <FileText className="w-3.5 h-3.5 text-white" />
            </div>
            <span className="text-xs font-black uppercase tracking-widest leading-none">Cover Letter<br /><span className="text-[#D3FF24]">Generator</span></span>
          </div>
        </div>
        {NAV.map(({ id, label, icon: Icon }) => (
          <button key={id} onClick={() => setSection(id)}
            className={`w-full flex items-center gap-2.5 px-4 py-2.5 text-xs transition border-l-2 text-left ${
              section === id
                ? "border-[#D3FF24] bg-white/[0.03] text-white"
                : "border-transparent text-neutral-500 hover:text-neutral-300 hover:bg-white/[0.02]"
            }`}>
            <Icon className="w-3.5 h-3.5 shrink-0" strokeWidth={1.75} />
            {label}
            {id === "letters" && letters.length > 0 && (
              <span className="ml-auto text-[10px] text-neutral-700">{letters.length}</span>
            )}
          </button>
        ))}
      </aside>

      {/* Content */}
      <main className="flex-1 px-10 py-10 max-w-5xl overflow-y-auto">
        {section === "dashboard"  && <DashboardSection letters={letters} loading={lettersLoading} setSection={setSection} />}
        {section === "generate"   && <GenerateSection linkedInProfile={linkedInProfile} onSaved={refreshLetters} />}
        {section === "letters"    && <LettersSection letters={letters} loading={lettersLoading} setSection={setSection}
            onDelete={id => setLetters(ls => ls.filter(l => l.id !== id))}
            onFavorite={(id, fav) => setLetters(ls => ls.map(l => l.id === id ? { ...l, is_favorite: fav } : l))} />}
        {section === "templates"  && <TemplatesSection setSection={setSection} onSelectTemplate={t => { setDefaults(d => ({ ...d, template: t })); }} />}
        {section === "settings"   && <SettingsSection defaults={defaults} setDefaults={setDefaults} />}
      </main>
    </div>
  );
}
